/**
 * Pre-rendered circle sprites, one per distinct colour.
 *
 * VOSviewer fills every circle with a vertical `GradientPaint` from a lightened
 * to a darkened variant of the item colour — light at the top, dark at the
 * bottom, both computed in Lab.  Building that gradient per circle per frame
 * would mean 2000 `createLinearGradient` calls and 4000 colour-stop
 * allocations every time the map moves.
 *
 * Instead each colour is rendered once into a small sprite and blitted.  A map
 * has at most eighteen cluster colours; an overlay quantises to the 256 entries
 * of its colour ramp, so the cache stays small and is warm after the first
 * frame.  The gradient is a smooth vertical ramp, so magnifying a 64-pixel
 * sprite up to a 60-pixel circle costs nothing visible.
 */

import type { RGB } from '../types';
import { css, darkColor, lightColor } from '../color/lab';
import { CIRCLE_DARK_TRANSITION, CIRCLE_LIGHT_TRANSITION } from '../model/constants';

const SPRITE_SIZE = 64;
/** Past this many entries the cache is dropped wholesale; it refills in a frame. */
const MAX_ENTRIES = 512;

export class CircleSprites {
  private cache = new Map<string, HTMLCanvasElement>();

  get(color: RGB, alpha: number): HTMLCanvasElement | null {
    const key = `${color[0]},${color[1]},${color[2]},${alpha.toFixed(3)}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    if (this.cache.size >= MAX_ENTRIES) this.cache.clear();

    const canvas = document.createElement('canvas');
    canvas.width = SPRITE_SIZE;
    canvas.height = SPRITE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const gradient = ctx.createLinearGradient(0, 0, 0, SPRITE_SIZE);
    gradient.addColorStop(0, css(lightColor(color, CIRCLE_LIGHT_TRANSITION), alpha));
    gradient.addColorStop(1, css(darkColor(color, CIRCLE_DARK_TRANSITION), alpha));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    // Inset by half a pixel so the antialiased edge stays inside the sprite and
    // does not pick up transparent neighbours when the sprite is scaled down.
    ctx.arc(SPRITE_SIZE / 2, SPRITE_SIZE / 2, SPRITE_SIZE / 2 - 0.5, 0, Math.PI * 2);
    ctx.fill();
    this.cache.set(key, canvas);
    return canvas;
  }

  clear(): void {
    this.cache.clear();
  }
}
